import { Box, Tooltip, TableCell, Typography, IconButton } from '@mui/material';

import { truncateText } from 'src/pages/chat/utils/text-formatting';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';
import { formatPrice } from 'src/pages/setting/components/billing/utils/format-price';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';

import { RowCellName } from './cells/row-cell-name';
import { RowCellDate } from './cells/row-cell-date';
import { RowCellLink } from './cells/row-cell-link';
import { RowCellText } from './cells/row-cell-text';
import { RowCellClid } from './cells/row-cell-clid';
import { RowCellCsat } from './cells/row-cell-csat';
import { RowCellPhone } from './cells/row-cell-phone';
import { RowCellSource } from './cells/row-cell-source';
import { RowCellStatus } from './cells/row-cell-status';
import { RowCellLabels } from './cells/row-cell-labels';
import { RowCellAssignee } from './cells/row-cell-assignee';
import { RowCellWebhookUrl } from './cells/row-cell-webhook-url';
import { RowCellResendMail } from './cells/row-cell-resend-mail';
import { RowCellLabelColor } from './cells/row-cell-label-color';
import { RowCellLearningQA } from './cells/row-cell-learning-qa';
import { RowCellDestination } from './cells/row-cell-destination';
import { RowCellMultiSelect } from './cells/row-cell-multiselect';
import { RowCellChannelName } from './cells/row-cell-channel-name';
import { RowCellUserProfile } from './cells/row-cell-user-profile';
import { RowCellInvoiceUtils } from './cells/row-cell-invoice-utils';
import { RowCellQuickActions } from './cells/row-cell-quick-actions';
import { RowCellPlayRecording } from './cells/row-cell-play-recording';
import { RowCellWorkflowSteps } from './cells/row-cell-workflow-steps';
import { RowCellLearningSource } from './cells/row-cell-learning-source';
import { RowCellLearningStatus } from './cells/row-cell-learning-status';
import { RowCellChannelActions } from './cells/row-cell-channel-actions';
import { RowCellWorkflowStatus } from './cells/row-cell-workflow-status';
import { RowCellLearningActions } from './cells/row-cell-learning-actions';
import { RowCellWorkflowActions } from './cells/row-cell-workflow-actions';
import { RowCellWorkflowTrigger } from './cells/row-cell-workflow-trigger';
import { RowCellCsatAgentScores } from './cells/row-cell-csat-agent-scores';
import { RowCellKnowledgeGapDetails } from './cells/row-cell-knowledge-gap-details';
import { RowCellKnowledgeGapActions } from './cells/row-cell-knowledge-gap-actions';

import type { TableHeadCellProps } from '../head/table-head-custom';

export type TableRowCellProps = {
  row: any;
  headCell: TableHeadCellProps;
  headerIndex: number;
  editHref: (id: string) => string;
  isSelectRecordDialog?: boolean;
  isSupportReport?: boolean;
  moduleName?: string;
  isTenantInvoice?: boolean;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  currentTenantId?: string;
  contactDetailHref?: (id: string) => string;
  accountDetailHref?: (id: string) => string;
  t?: any;
  isSuperAdmin?: boolean;
  tenantUserDetailsHref?: (tenantId: string, userId: string) => string;
  handleAddFriend?: (row: any) => void;
  handleCustomiseDID?: (id: string) => void;
  handleIVRConnection?: (id: string) => void;
  handleEditContact?: (id: string) => string | Promise<void>;
  handleDeleteRecord?: (id: string) => void;
  isEdit?: boolean;
  isDelete?: boolean;
  currentlyPlayingId?: string | null;
  playCallRecording?: (id: string) => void;
  handlePhoneNumberClick?: (number: any) => void;
  handleResendMail?: (id: string) => string;
  handleLoginAsUser?: (row: any) => void;
  handlePreview?: (id: any) => void;
  isCampaignTamplate?: boolean;
  handlePayNowButton?: (id: any) => void;
  invoiceLockedMessage?: string;
  handleApproveLearning?: (id: string) => void | Promise<void>;
  handleRejectLearning?: (id: string) => void | Promise<void>;
  handleResolveKnowledgeGap?: (id: string) => void | Promise<void>;
  isApprovingLearning?: boolean;
  isRejectingLearning?: boolean;
  isResolvingKnowledgeGap?: boolean;
  handleToggleStatus?: (row: any) => void;
  handleQuickEdit?: (row: any) => void;
  menuActions: any;
  openinvoiceUtils: (event: React.MouseEvent<HTMLElement>, row: any) => void;
  handleOpenConversation?: (row: any) => void;
  userRole?: string;
  baseTabs: any[];
  rowInteractionLocked?: boolean;
  rowInteractionLockedMessage?: string;
  handleRefresh?: (id: string) => void;
};

export function TableRowCell({
  row,
  headCell,
  headerIndex,
  editHref,
  isSelectRecordDialog,
  isSupportReport,
  moduleName,
  isTenantInvoice,
  tenantInvoiceDetailsHref,
  currentTenantId,
  contactDetailHref,
  accountDetailHref,
  t,
  isSuperAdmin,
  tenantUserDetailsHref,
  handleAddFriend,
  handleCustomiseDID,
  handleIVRConnection,
  handleEditContact,
  handleDeleteRecord,
  isEdit,
  isDelete,
  currentlyPlayingId,
  playCallRecording,
  handlePhoneNumberClick,
  handleResendMail,
  handleLoginAsUser,
  handlePreview,
  isCampaignTamplate,
  handlePayNowButton,
  invoiceLockedMessage = '',
  handleApproveLearning,
  handleRejectLearning,
  handleResolveKnowledgeGap,
  isApprovingLearning,
  isRejectingLearning,
  isResolvingKnowledgeGap,
  handleToggleStatus,
  handleQuickEdit,
  menuActions,
  openinvoiceUtils,
  handleOpenConversation,
  userRole,
  baseTabs,
  rowInteractionLocked = false,
  rowInteractionLockedMessage = '',
  handleRefresh,
}: TableRowCellProps) {
  const { isView: isCompanyView } = useModulePermissions('company');
  const { isView: isContactView } = useModulePermissions('contact');
  const key = headCell.id;
  const rawValue = row[key];
  const value =
    (key === 'actionPerformed' || key === 'module') && moduleName === 'AUDIT_LOG'
      ? t(String(rawValue).toUpperCase(), { defaultValue: rawValue })
      : rawValue;

  const udatedValue =
    value && value.length > 20 ? (
      <Tooltip title={value}>
        <Typography component="span" sx={{ fontSize: 'inherit' }}>
          {truncateText(value, 20)}
        </Typography>
      </Tooltip>
    ) : (
      value
    );

  if (moduleName === 'CSAT') {
    return (
      <RowCellCsat
        columnId={key}
        row={row}
        headerIndex={headerIndex}
        t={t}
        handlePreview={handlePreview}
      />
    );
  }

  if (moduleName === 'CSAT_AGENT_SCORES') {
    return (
      <RowCellCsatAgentScores
        columnId={key}
        row={row}
        headerIndex={headerIndex}
        t={t}
        handlePreview={handlePreview}
      />
    );
  }

  if (moduleName === 'LEARNING_QUEUE') {
    if (key === 'question' || key === 'answer') {
      return <RowCellLearningQA key={key} value={value} headerIndex={headerIndex} />;
    }

    if (key === 'source') {
      return (
        <RowCellLearningSource
          key={key}
          source={row.source}
          headerIndex={headerIndex}
          baseTabs={baseTabs}
        />
      );
    }

    if (key === 'status') {
      return (
        <RowCellLearningStatus
          key={key}
          status={row.status}
          headerIndex={headerIndex}
          baseTabs={baseTabs}
        />
      );
    }

    if (key === 'action') {
      return (
        <RowCellLearningActions
          rowId={row.id}
          status={row.status}
          key={key}
          headerIndex={headerIndex}
          handleApprove={handleApproveLearning}
          handleReject={handleRejectLearning}
          isApproving={isApprovingLearning}
          isRejecting={isRejectingLearning}
        />
      );
    }
  }

  if (moduleName === 'KNOWLEDGE_GAPS') {
    if (key === 'exitReason' || key === 'clusterCount') {
      return (
        <RowCellKnowledgeGapDetails key={key} row={row} headerIndex={headerIndex} fieldKey={key} />
      );
    }

    if (key === 'action') {
      return (
        <RowCellKnowledgeGapActions
          key={key}
          rowId={row.id}
          headerIndex={headerIndex}
          handleResolve={handleResolveKnowledgeGap}
          isResolving={isResolvingKnowledgeGap}
        />
      );
    }
  }

  if (moduleName === 'WORKFLOW') {
    if (key === 'trigger') {
      return (
        <TableCell key={key || headerIndex}>
          <RowCellWorkflowTrigger trigger={row.trigger} t={t} />
        </TableCell>
      );
    }

    if (key === 'steps') {
      return (
        <TableCell key={key || headerIndex} align="center">
          <RowCellWorkflowSteps nodeCount={row.nodes?.length ?? 0} t={t} />
        </TableCell>
      );
    }

    if (key === 'status') {
      return (
        <TableCell key={key || headerIndex}>
          <RowCellWorkflowStatus
            row={row}
            handleToggleStatus={isEdit ? handleToggleStatus : undefined}
            t={t}
          />
        </TableCell>
      );
    }

    if (key === 'executions') {
      return (
        <TableCell key={key || headerIndex} align="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {row._count?.executions ?? 0}
          </Typography>
        </TableCell>
      );
    }

    if (key === 'type') {
      return (
        <TableCell key={key || headerIndex}>
          <Label variant="soft" color="info" sx={{ textTransform: 'uppercase', fontSize: 10 }}>
            {t(row.type)}
          </Label>
        </TableCell>
      );
    }

    if (key === 'action') {
      return (
        <TableCell key={key || headerIndex} align="right">
          <RowCellWorkflowActions
            row={row}
            onEdit={(id) => (handleEditContact ? handleEditContact(id) : null)}
            onQuickEdit={handleQuickEdit}
            onDelete={(id) => handleDeleteRecord?.(id)}
            isEdit={isEdit}
            isDelete={isDelete}
            t={t}
          />
        </TableCell>
      );
    }
  }

  if (
    [
      'name',
      'username',
      'firstName',
      'lastName',
      'account_name',
      'userName',
      'teamName',
      'title',
      'email',
      'ticketNo',
      'id',
      'performedBy',
      'actionPerformed',
      'module',
    ].includes(key) &&
    moduleName?.toLowerCase() !== 'user'
  ) {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellName
          row={row}
          value={udatedValue}
          headCellId={key}
          editHref={editHref}
          isSelectRecordDialog={isSelectRecordDialog}
          isSupportReport={isSupportReport}
          moduleName={moduleName}
          isTenantInvoice={isTenantInvoice}
          tenantInvoiceDetailsHref={tenantInvoiceDetailsHref}
          currentTenantId={currentTenantId}
          handleEditContact={handleEditContact}
        />
      </TableCell>
    );
  }

  if (key === 'contactId' || key === "contactName") {
    if (!isContactView) {
      return (
        <TableCell key={key || headerIndex}>
          <Tooltip title={t('CONTACT_ACCESS_RESTRICTED') || 'Contact access is restricted. Please contact your administrator.'} arrow placement="top">
            <Typography variant="body2" sx={{ color: 'text.disabled', cursor: 'not-allowed', display: 'inline-block' }}>
              {udatedValue}
            </Typography>
          </Tooltip>
        </TableCell>
      );
    }

    const href =
      'contact' in row && row.contact?.id && contactDetailHref
        ? contactDetailHref(row.contact.id)
        : '#';

    return (
      <TableCell key={key || headerIndex}>
        <RowCellLink
          value={udatedValue}
          href={href}
          isSelectRecordDialog={isSelectRecordDialog}
          isSupportReport={isSupportReport}
        />
      </TableCell>
    );
  }

  if (key === 'accountName') {
    if (!isCompanyView) {
      return (
        <TableCell key={key || headerIndex}>
          <Tooltip
            title={
              t('ACCOUNT_ACCESS_RESTRICTED') ||
              'Account access is restricted. Please contact your administrator.'
            }
            arrow
            placement="top"
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.disabled', cursor: 'not-allowed', display: 'inline-block' }}
            >
              {udatedValue}
            </Typography>
          </Tooltip>
        </TableCell>
      );
    }

    const href =
      ('companyId' in row || ('contact' in row && row.contact?.company?.id)) && accountDetailHref
        ? accountDetailHref('contact' in row ? row.contact?.company?.id : row.companyId)
        : '#';

    return (
      <TableCell key={key || headerIndex}>
        <RowCellLink
          value={udatedValue}
          href={href}
          isSelectRecordDialog={isSelectRecordDialog}
          isSupportReport={isSupportReport}
        />
      </TableCell>
    );
  }

  if (key === 'assignUserListName') {
    const assignees: { id: string; type: 'USER' | 'TEAM'; name: string }[] = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [];

    return (
      <TableCell key={key || headerIndex}>
        <RowCellAssignee
          assignees={assignees}
          isSelectRecordDialog={isSelectRecordDialog}
          isSupportReport={isSupportReport}
          t={t}
        />
      </TableCell>
    );
  }

  if (
    ['name', 'username', 'firstName', 'account_name', 'userName', 'teamName', 'email'].includes(
      key
    ) &&
    moduleName?.toLowerCase() === 'user'
  ) {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellUserProfile
          row={row}
          headCellId={key}
          isSuperAdmin={isSuperAdmin ?? false}
          editHref={editHref}
          tenantUserDetailsHref={tenantUserDetailsHref}
          currentTenantId={currentTenantId}
          handleEditContact={handleEditContact}
          moduleName={moduleName}
        />
      </TableCell>
    );
  }

  if (
    [
      'createdAt',
      'updatedAt',
      'callDateTs',
      'createdDate',
      'due_date',
      'date',
      'issuedDate',
      'lastModified',
      'createdTime',
      'lastActivity',
      'dueDate',
    ].includes(key)
  ) {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellDate value={value} fieldKey={key} />
      </TableCell>
    );
  }

  if (key === 'channelName') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellChannelName
          value={value}
          row={row}
          isSelectRecordDialog={isSelectRecordDialog}
          isSupportReport={isSupportReport}
          interactionLocked={rowInteractionLocked}
          restrictionMessage={rowInteractionLockedMessage}
        />
      </TableCell>
    );
  }

  if (key === 'source') {
    return (
      <TableCell key={key}>
        <RowCellSource row={row} contactDetailHref={contactDetailHref} />
      </TableCell>
    );
  }

  if (key === 'destination') {
    return (
      <TableCell key={key}>
        <RowCellDestination row={row} contactDetailHref={contactDetailHref} />
      </TableCell>
    );
  }

  if (key === 'url') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellWebhookUrl row={row} t={t} />
      </TableCell>
    );
  }

  if (key === 'action') {
    const isDisabled =
      'campaignStatus' in row && row.campaignStatus
        ? ['completed', 'running'].includes(String(row?.campaignStatus))
        : false;
    const isBuiltInRole =
      moduleName?.toLowerCase() === 'role' && 'isCustom' in row && row.isCustom === false;

    const channelRowLocked = rowInteractionLocked && moduleName === 'CHANNEL';

    return (
      <TableCell key={key || headerIndex}>
        <RowCellChannelActions
          row={row}
          handleAddFriend={handleAddFriend}
          handleCustomiseDID={handleCustomiseDID}
          handleIVRConnection={handleIVRConnection}
          handleEditContact={handleEditContact}
          handleDeleteRecord={handleDeleteRecord}
          isEdit={isEdit}
          isDelete={isDelete}
          isDisabled={isDisabled}
          interactionLocked={channelRowLocked}
          lockTooltip={rowInteractionLockedMessage}
          disableDelete={isBuiltInRole}
          deleteTooltip={isBuiltInRole ? t?.('BUILTIN_ROLE_DELETE_ERROR') : undefined}
          t={t}
        />
      </TableCell>
    );
  }

  if (key === 'playRecording') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellPlayRecording
          row={row}
          currentlyPlayingId={currentlyPlayingId ?? null}
          playCallRecording={playCallRecording}
          t={t}
        />
      </TableCell>
    );
  }

  if (key === 'phone') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellPhone value={value} row={row} handlePhoneNumberClick={handlePhoneNumberClick} />
      </TableCell>
    );
  }

  if (key === 'clid') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellClid value={value} row={row} contactDetailHref={contactDetailHref} />
      </TableCell>
    );
  }

  if (['status', 'labelType', 'priority', 'bill_status'].includes(key)) {
    // Note: merged priority and bill_status checks as they seemed to use the exact same component and logic
    if (
      (key === 'priority' && !('priority' in row)) ||
      (key === 'bill_status' && !('bill_status' in row))
    ) {
      // fall through if key matches but property doesn't exist?
      // Original code had specific checks: if (key === 'priority' && 'priority' in row)
      // If it doesn't exist, what happens? It falls through to default.
      // So I need to be careful with the merge.
      // But here I'll just check existence.
    } else {
      return (
        <TableCell key={key || headerIndex}>
          <RowCellStatus
            row={row}
            headCellId={key}
            value={udatedValue}
            moduleName={moduleName}
            t={t}
            baseTabs={baseTabs}
          />
        </TableCell>
      );
    }
  }

  // Handle priority specific check from original code
  // Oh wait, I see I merged them above but logic was slightly different in original:
  // if (key === 'priority' && 'priority' in row) -> RowCellStatus
  // if (key === 'bill_status' && 'bill_status' in row) -> RowCellStatus
  // if (['status', 'labelType'].includes(key)) -> RowCellStatus
  // I can combine them carefully.

  if (key === 'labelColor' && 'labelColor' in row) {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellLabelColor row={row} />
      </TableCell>
    );
  }

  if (key === 'labels') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellLabels row={row} />
      </TableCell>
    );
  }

  if (key === 'resendMail' && isEdit) {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellResendMail
          row={row}
          handleEditContact={handleEditContact}
          handleResendMail={handleResendMail}
          handleLoginAsUser={handleLoginAsUser}
          handleDeleteRecord={handleDeleteRecord}
          moduleName={moduleName}
          userRole={userRole}
          isDelete={isDelete}
          t={t}
        />
      </TableCell>
    );
  }

  if (key === 'direction') {
    return <TableCell key={key || headerIndex}>{value}</TableCell>;
  }

  if (key === 'amount_due' || key === 'total') {
    return <TableCell key={key || headerIndex}>{formatPrice(value)}</TableCell>;
  }

  if (key === 'costUSD') {
    const n = Number(value);
    let costText = '—';
    if (Number.isFinite(n)) {
      if (n === 0) costText = '$0.00';
      else if (Math.abs(n) < 0.01) costText = `$${n.toFixed(6)}`;
      else costText = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    }
    return <TableCell key={key || headerIndex}>{costText}</TableCell>;
  }

  if (key === 'status' && moduleName === 'AI_USAGE') {
    const err = (row as { errorMessage?: string | null })?.errorMessage;
    const label = value ?? '';
    return (
      <TableCell key={key || headerIndex}>
        {err && label !== 'success' ? (
          <Tooltip title={err}>
            <span>{label}</span>
          </Tooltip>
        ) : (
          label
        )}
      </TableCell>
    );
  }

  if (key === 'tamplateName') {
    return <TableCell key={key || headerIndex}>{value}</TableCell>;
  }

  if (key === 'role') {
    return (
      <TableCell key={key || headerIndex}>
        {(row as any)?.role ?? (row as any)?.roles?.[0]?.name ?? ''}
      </TableCell>
    );
  }

  if (key === 'reportToUser') {
    return (
      <TableCell key={key || headerIndex}>
        {(row as any)?.reportToUser?.firstName || (row as any)?.reportToUser?.lastName
          ? `${(row as any)?.reportToUser?.firstName || ''} ${(row as any)?.reportToUser?.lastName || ''}`
          : (row as any)?.reportToUser?.email || ''}
      </TableCell>
    );
  }

  if (key === 'previewAction') {
    return (
      <TableCell key={key || headerIndex}>
        <RowCellQuickActions
          row={row}
          handleEditContact={handleEditContact}
          handleDeleteRecord={handleDeleteRecord}
          handlePreview={handlePreview}
          handleRefresh={handleRefresh}
          isDelete={isDelete}
          isCampaignTamplate={!!isCampaignTamplate}
          isEdit={isEdit}
          t={t}
        />
      </TableCell>
    );
  }

  if (key === 'description') {
    return (
      <RowCellText value={value} maxLength={20} parseHtml headerIndex={headerIndex} cellKey={key} />
    );
  }

  if (key === 'bill_name') {
    return <RowCellText value={value} maxLength={50} headerIndex={headerIndex} cellKey={key} />;
  }

  if (key === 'addonName') {
    return <RowCellText value={value} maxLength={50} headerIndex={headerIndex} cellKey={key} />;
  }

  if (key === 'channelType') {
    return (
      <RowCellText
        value={value}
        maxLength={50}
        transformValue={(v) => v.replace(/_/g, ' ')}
        headerIndex={headerIndex}
        cellKey={key}
      />
    );
  }

  if (key === 'invoiceUtils') {
    return (
      <TableCell align="right" sx={{ px: 1 }} key={key || headerIndex}>
        <RowCellInvoiceUtils
          row={row}
          isTenantInvoice={isTenantInvoice}
          handlePayNowButton={handlePayNowButton}
          lockedMessage={invoiceLockedMessage}
          menuActions={menuActions}
          openinvoiceUtils={(e, r) => openinvoiceUtils(e, r)}
          t={t}
        />
      </TableCell>
    );
  }

  if (key === 'redirect') {
    return (
      <TableCell
        key={key || headerIndex}
        sx={{
          p: 1,
          textAlign: 'center',
          verticalAlign: 'middle',
          width: 60,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: 40,
          }}
        >
          <Tooltip title={t ? t('REDIRECT_INBOX') : 'Redirect to Inbox'}>
            <IconButton
              onClick={() => handleOpenConversation?.(row)}
              sx={{
                p: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Iconify icon="tabler-message-forward" width={24} height={24} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    );
  }

  // Handle boolean values - convert to Yes/No
  let cellContent = udatedValue;
  if (typeof value === 'boolean') {
    cellContent = value ? 'Yes' : 'No';
  }

  // Check if it's a multiSelect field
  const multiSelectContent =
    headCell.type === 'multiSelect' ? (
      <RowCellMultiSelect value={value} headCell={headCell} />
    ) : null;

  return (
    <TableCell
      key={key || headerIndex}
      sx={{
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          color: 'primary.main',
        },
      }}
    >
      {multiSelectContent || cellContent}
    </TableCell>
  );
}
